/*
  tr-quick-add.js — le « + » des cartes produit.

  Déplie les tailles sur la carte et ajoute au panier sans quitter la
  page. Un seul élément personnalisé sert toutes les cartes du thème :
  accueil, collection, recherche, « compléter la tenue ».

  Le compteur du panier est relu depuis /cart.js plutôt qu'incrémenté à
  l'aveugle : si l'ajout échoue côté serveur — rupture de stock gagnée
  de vitesse, limite de quantité — le compteur ne ment pas.
*/

if (!customElements.get('tr-quick-add')) {
  customElements.define(
    'tr-quick-add',
    class TrQuickAdd extends HTMLElement {
      connectedCallback() {
        this.trigger = this.querySelector('.tr-card__quick-trigger');
        this.panel = this.querySelector('.tr-card__quick-panel');
        if (!this.trigger || !this.panel) return;

        this.trigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.toggle();
        });

        this.panel.querySelectorAll('.tr-card__quick-size').forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            this.add(button);
          });
        });

        // Un seul panneau ouvert à la fois, et l'ouvert se referme dès
        // qu'on touche ailleurs : sur une grille à deux colonnes, deux
        // panneaux ouverts se chevauchent et on ne sait plus lequel agit.
        this.onOutside = (event) => {
          if (!this.contains(event.target)) this.close();
        };
        this.onEscape = (event) => {
          if (event.key === 'Escape') this.close();
        };
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onOutside);
        document.removeEventListener('keydown', this.onEscape);
      }

      toggle() {
        this.classList.contains('is-open') ? this.close() : this.open();
      }

      open() {
        document.querySelectorAll('tr-quick-add.is-open').forEach((other) => {
          if (other !== this) other.close();
        });
        this.classList.add('is-open');
        this.panel.hidden = false;
        this.trigger.setAttribute('aria-expanded', 'true');
        document.addEventListener('click', this.onOutside);
        document.addEventListener('keydown', this.onEscape);
      }

      close() {
        this.classList.remove('is-open');
        this.panel.hidden = true;
        this.trigger.setAttribute('aria-expanded', 'false');
        document.removeEventListener('click', this.onOutside);
        document.removeEventListener('keydown', this.onEscape);
      }

      async add(button) {
        const id = button.dataset.variantId;
        if (!id || this.classList.contains('is-busy')) return;

        this.classList.add('is-busy');
        try {
          const response = await fetch(this.dataset.url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
          });
          if (!response.ok) throw new Error('add failed');

          await TrQuickAdd.refreshCartCount();
          document.dispatchEvent(new CustomEvent('tr:cart:updated'));
          this.classList.add('is-done');
          window.setTimeout(() => {
            this.classList.remove('is-done');
            this.close();
          }, 900);
        } catch (error) {
          // L'échec doit rester lisible sur la carte : le bouton se barre
          // plutôt que de laisser croire à un ajout qui n'a pas eu lieu.
          button.disabled = true;
          button.classList.add('is-unavailable');
        } finally {
          this.classList.remove('is-busy');
        }
      }

      static async refreshCartCount() {
        try {
          const cart = await (await fetch(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart.js` : '/cart.js')).json();
          document.querySelectorAll('[data-cart-count]').forEach((node) => {
            node.textContent = cart.item_count;
            node.classList.toggle('is-empty', cart.item_count === 0);
          });
        } catch (error) {
          /* Un compteur non rafraîchi ne justifie pas de casser la page. */
        }
      }
    }
  );
}
